import * as THREE from "three";
import type { Scenery } from "../../prims/scenery";
import { mergeStatic } from "../merge";
import { onTick, type Kit } from "./kit";

// A carousel (#156): a round deck of horses on brass poles under a striped
// canopy, going round on the render clock while the horses ride up and down.
// `size` scales it; about 11 m across at 1.

const HORSE_COLORS = ["#f4f1ea", "#3d2c2e", "#c9a66b", "#f4f1ea", "#8d6e63", "#f4f1ea"];
const STRIPES = ["#e63946", "#f4f1ea"];
/** Radians a second: a turn every 12 s or so. */
const SPIN = 0.5;
const R = 5;

function horse(kit: Kit, color: string): THREE.Group {
  const g = new THREE.Group();
  kit.box(g, 1.1, 0.42, 0.34, color, 0, 0, 0, "plastic"); // body
  kit.box(g, 0.24, 0.5, 0.24, color, 0.55, 0.25, 0, "plastic"); // neck
  kit.box(g, 0.42, 0.22, 0.22, color, 0.75, 0.62, 0, "plastic"); // head
  kit.box(g, 0.2, 0.12, 0.36, "#e63946", 0, 0.42, 0, "leather"); // saddle
  for (const [x, z] of [
    [0.42, 0.11],
    [0.42, -0.11],
    [-0.42, 0.11],
    [-0.42, -0.11],
  ] as const)
    kit.box(g, 0.1, 0.5, 0.1, color, x, -0.5, z, "plastic");
  return g;
}

export function carousel(kit: Kit, _s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const base = new THREE.Group();
  kit.cyl(base, R + 0.4, R + 0.6, 0.3, "#c9b79c", 0, 0, 0, 32, "concrete");
  root.add(mergeStatic(base));

  // Everything that turns: deck, centre column, canopy, the poles.
  const top = new THREE.Group();
  kit.cyl(top, R, R, 0.35, "#b5651d", 0, 0.3, 0, 32, "plywood");
  kit.cyl(top, 1.1, 1.1, 3.6, "#ffd166", 0, 0.65, 0, 16);
  kit.stripedDrum(top, R + 0.1, 0.7, 4.25, 24, ["#ffd166", "#e63946"]);
  kit.stripedCone(top, R + 0.6, 2.4, 4.95, 16, STRIPES);
  kit.ball(top, 0.35, "#ffd166", 0, 7.6, 0);
  const poles = 8;
  const brass = "#d4a017";
  for (let i = 0; i < poles; i++) {
    const a = (i / poles) * Math.PI * 2;
    const [x, z] = [Math.cos(a) * (R - 1.2), Math.sin(a) * (R - 1.2)];
    kit.rod(top, new THREE.Vector3(x, 0.65, z), new THREE.Vector3(x, 4.25, z), 0.05, brass);
  }
  const spun = mergeStatic(top);
  root.add(spun);

  // The horses ride their poles up and down as they go round.
  const horses: THREE.Group[] = [];
  for (let i = 0; i < poles; i++) {
    const h = mergeStatic(horse(kit, HORSE_COLORS[i % HORSE_COLORS.length]!));
    root.add(h);
    horses.push(h);
  }
  const place = (t: number) => {
    const turn = t * SPIN;
    spun.rotation.y = turn;
    horses.forEach((h, i) => {
      const a = (i / poles) * Math.PI * 2 - turn;
      h.position.set(Math.cos(a) * (R - 1.2), 1.9 + Math.sin(t * 2 + i * 1.7) * 0.35, Math.sin(a) * (R - 1.2));
      // Facing the way round it's going.
      h.rotation.y = Math.PI / 2 - a;
    });
  };
  place(0);
  onTick(root, place);
  return root;
}
